import * as THREE from "three";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";

import Representation3d from "../representation3d/Representation3d";
import ProductData from "../productData/ProductData";
import MaterialPair from "../material/MaterialPair";
import { getLayerForProductRepresentation3d } from "../../utils/layerUtils";
import Representation3dTypeEnum from "../representation3d/Representation3dTypeEnum";
import TriangleMeshGeometry from "../geometry/TriangleMeshGeometry";
import TriangleMesh from "../representation3d/TriangleMesh";
import ExtrudedAreaSolidGeometry from "../geometry/ExtrudedAreaSolidGeometry";
import ExtrudedAreaSolid from "../representation3d/ExtrudedAreaSolid";
import FbxAsset from "../representation3d/FbxAsset";
import FbxAsyncGeometry from "../geometry/FbxAsyncGeometry";
import IProduct from "../../interfaces/IProduct";
import IPlacement3d from "../../interfaces/IPlacement3d";
import { USE_SHADOWS } from "../../components/Stage3d";

/**
 * Rotates UV coordinates of a geometry around a specified center point
 * using a 3D rotation defined by an arbitrary axis and angle.
 *
 * UVs are treated as points in the XY plane (Z=0).
 *
 * @param geometry - The BufferGeometry containing UVs
 * @param axis - Normalized or non-normalized rotation axis (will be normalized internally)
 * @param angleRad - Rotation angle in radians (positive = counterclockwise when looking along axis)
 * @param center - Center point of rotation in UV space (default: 0.5, 0.5)
 */
export function rotateUVs(
  geometry: THREE.BufferGeometry,
  axis: THREE.Vector3,
  angleRad: number,
  center: THREE.Vector2 = new THREE.Vector2(0.5, 0.5)
): void {
  // Early validation
  if (!geometry.hasAttribute('uv')) {
    console.warn('Geometry has no "uv" attribute');
    return;
  }

  const uvAttr = geometry.getAttribute('uv');
  if (!(uvAttr instanceof THREE.BufferAttribute) || uvAttr.itemSize !== 2) {
    console.warn('UV attribute must be a 2-component BufferAttribute');
    return;
  }

  const uvArray = uvAttr.array as Float32Array;

  // Prepare rotation (only needs to be computed once)
  const quaternion = new THREE.Quaternion().setFromAxisAngle(
    axis.clone().normalize(), // safe even if input isn't normalized
    angleRad
  );

  //const rotationMatrix = new THREE.Matrix3().setFromQuaternion(quaternion);
  const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(quaternion);

  // Reusable vector for transformations
  const point = new THREE.Vector3();
  const center3 = new THREE.Vector3(center.x, center.y, 0);

  // Transform each UV coordinate
  for (let i = 0; i < uvArray.length; i += 2) {
    // Read UV → make it Vector3 (z=0)
    point.x = uvArray[i];
    point.y = uvArray[i + 1];
    point.z = 0;

    // 1. Move to origin
    point.sub(center3);

    // 2. Apply rotation
    point.applyMatrix4(rotationMatrix);

    // 3. Move back
    point.add(center3);

    // Write updated UV (ignore resulting z)
    uvArray[i] = point.x;
    uvArray[i + 1] = point.y;
  }

  try {
    // Mark as changed
    uvAttr.needsUpdate = true;

    // === NEW: Make the geometry indexed if it isn't already ===
    if (!geometry.index) {
      //console.log('Geometry was not indexed → merging vertices to create index');
      const merged = BufferGeometryUtils.mergeVertices(geometry);  // mutates geometry in place
      geometry.copy(merged);
      // Optional: tighter tolerance if you have very close-but-different verts
      // BufferGeometryUtils.mergeVertices(geometry, 1e-5);
    }
    
    if (!geometry.attributes.position || !geometry.attributes.normal || !geometry.attributes.uv) {
      console.warn('Missing required attrib for tangents:', {
        position: !!geometry.attributes.position,
        normal: !!geometry.attributes.normal,
        uv: !!geometry.attributes.uv,
      });
    }

    // Very important when using normal maps / tangent space lighting
    geometry.computeTangents();
  } catch (error) {
    //Missing required attributes (index, position, normal or uv)
    console.error("Failed to update UV attribute", error);
  }

}

function rotateUVsAroundCenter(
  geometry: THREE.BufferGeometry,
  angleRad: number,          // e.g. THREE.MathUtils.degToRad(45)
  center: THREE.Vector2 = new THREE.Vector2(0.5, 0.5)
) {
  if (!geometry.hasAttribute('uv')) {
    console.warn('Geometry has no uv attribute');
    return;
  }

  const uvAttr = geometry.getAttribute('uv') as THREE.BufferAttribute;
  if (!(uvAttr instanceof THREE.BufferAttribute) || uvAttr.itemSize !== 2) {
    console.warn('uv attribute is not a 2-component BufferAttribute');
    return;
  }

  const uvArray = uvAttr.array as Float32Array; // usually Float32

  // Precompute rotation matrix values
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);

  for (let i = 0; i < uvArray.length; i += 2) {
    const u = uvArray[i];
    const v = uvArray[i + 1];

    // Translate → rotate → translate back
    const dx = u - center.x;
    const dy = v - center.y;

    const uRot = center.x + (dx * c - dy * s);
    const vRot = center.y + (dx * s + dy * c);

    uvArray[i]     = uRot;
    uvArray[i + 1] = vRot;
  }

  uvAttr.needsUpdate = true;

  // Optional but often needed after UV change (especially with normal maps / tangent space)
  if (geometry.attributes.normal)
    geometry.attributes.normal.needsUpdate = true;   // if you also touch normals

  geometry.computeTangents();                       // ← very important for normal maps!
}


const reorientUvMap = (placement: IPlacement3d, representationObject: THREE.Mesh) => {

    const geometry = representationObject.geometry;
    geometry.computeVertexNormals(); // often helpful

    // Get or create uv attribute
    let uv = geometry.getAttribute('uv') as THREE.BufferAttribute;
    if (!uv) {
      uv = new THREE.BufferAttribute(new Float32Array(geometry.attributes.position.count * 2), 2);
      geometry.setAttribute('uv', uv);
    }

    const pos = geometry.attributes.position as THREE.BufferAttribute;

    let minX = Infinity, maxX = -Infinity;
    let minSecondary = Infinity, maxSecondary = -Infinity;

    // Find bounds — decide secondary axis (usually Y or Z depending on model orientation)
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getY(i);
      const y = pos.getX(i);
      //const z = pos.getZ(i);

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);

      // Example: use Y as vertical direction (change to Z if your model is more flat in Y)
      const secondary = y; // ← or z
      minSecondary = Math.min(minSecondary, secondary);
      maxSecondary = Math.max(maxSecondary, secondary);
    }

    const xRange = maxX - minX || 1;
    const secRange = maxSecondary - minSecondary || 1;

    // Optional: aspect correction factor (try 0.5–2.0 depending on texture/model proportions)
    const textureAspectCorrection = 1.0;

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getY(i);
      const y = pos.getX(i);
      //const z = pos.getZ(i);

      const u = (x - minX) / xRange;

      // V from secondary axis (Y here — swap with Z if needed)
      let v = (y - minSecondary) / secRange;

      // Optional: flip V direction if texture appears upside-down
      // v = 1.0 - v;

      // Optional: scale V to better match texture aspect
      v = (v - 0.5) * textureAspectCorrection + 0.5;

      uv.setXY(i, u, v);
    }

    uv.needsUpdate = true;
}


const createProductRepresentation3d = ( representation3d : Representation3d, 
                                        product: IProduct, 
                                        materialPair: MaterialPair) : THREE.Object3D | null => {
    
    const layer = getLayerForProductRepresentation3d(   product.productData.productType, 
                                                        representation3d.representationType, 
                                                        product.productData);

    let representationObject: THREE.Object3D | null = null;

    switch (representation3d.representationType) {
        case Representation3dTypeEnum.TriangleMesh:
            representationObject = TriangleMeshGeometry.create( representation3d as TriangleMesh, 
                                                materialPair.getThreeJsMaterial(), 
                                                layer);
            break;

        case Representation3dTypeEnum.ExtrudedAreaSolid:

            const eas = representation3d as ExtrudedAreaSolid;
            representationObject = ExtrudedAreaSolidGeometry.create( eas, 
                                                        materialPair.getThreeJsMaterial(), 
                                                        layer);

            if (representationObject instanceof THREE.Mesh) {
                rotateUVs(representationObject.geometry, eas.extrudedDirection, Math.PI / 2);
            }
                                                    
            break;
        
        case Representation3dTypeEnum.FbxAsset:
            const fbxRepr = representation3d as FbxAsset;
            const fbxGeom = new FbxAsyncGeometry(   fbxRepr.path, 
                                                    layer, 
                                                    fbxRepr.placement.location,
                                                    product.dimensions,
                                                    {
                                                        scaleToFit: fbxRepr.scaleToFit,
                                                        isMirrored: fbxRepr.isMirrored,
                                                        rotation: fbxRepr.placement.getRotationQuaternion()
                                                    }
                                                );
            representationObject = fbxGeom.group;
            break;

        case Representation3dTypeEnum.NotDefined:
        default:
            return null;

    }

    if (representationObject instanceof THREE.Mesh) {
        //reorientUvMap(product.placement, representationObject as THREE.Mesh);
        //rotateUVsAroundCenter(representationObject.geometry, Math.PI / 2);
        //rotateUVs(representationObject.geometry, product.placement.axis, Math.PI / 2);
    }

    if (USE_SHADOWS) {
      representationObject?.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
              child.castShadow = true;
              child.receiveShadow = true;
          }
      });
    }
    
    return representationObject;
};

export { createProductRepresentation3d };

